import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs/index';
import panzoom, { PanZoom, PanZoomOptions } from 'panzoom';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Store } from '@ngxs/store';

import { AutoZoomBox, PuzzleZoomService } from 'projects/tools/src/lib/shared-module/puzzle-zoom/puzzle-zoom.service';
import { HelperFunctionsService } from 'projects/tools/src/lib/shared-module/helpers/helper-functions.service';
import { SetZoom } from 'projects/tools/src/lib/store/puzzle-zoom.actions';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { ResizeObserver } from '@juggle/resize-observer';

import { PuzzleKeyboardService } from '../puzzle-softkeyboard/puzzle-keyboard.service';

interface DOMRect extends DOMRectReadOnly {
  height: number;
  width: number;
  x: number;
  y: number;
}

@Directive({
  selector: '[libAppPuzzleZoom]',
  standalone: true,
})
export class PuzzleZoomDirective implements OnDestroy, OnInit {
  @Input() private clientRect: DOMRect;
  @Input() boardWidth: number;
  @Input() boardHeight: number;
  @Input() private shiftPan = false;
  @Input() private deactivate = false;
  @Input() availableHeight = 0;

  private hostRect: DOMRect;

  @HostBinding('style.transform')
  public zoomString: string;
  private globalZoomSubscription: Subscription;
  private panZoomController: PanZoom;
  private globalZoomValue = 1;
  private initialized = false;

  private setGlobalZoomTimeout: number;

  private mouseDown = false;

  private transformTimeout: any;

  private printZoom$: Subscription;
  private autoZoom$: Subscription;
  private scaleTimeout: number;
  private hasNativeKeyboard: boolean;

  // private parentHeight: number;
  // private parentHeightChangedTimeout: number;
  // private parentHeightChanged: EventEmitter<number> = new EventEmitter<number>();
  private softKeyboardOpen = false;

  constructor(
    private cd: ChangeDetectorRef,
    private el: ElementRef,
    private service: PuzzleZoomService,
    private deviceService: DeviceDetectorService,
    private helperFunctions: HelperFunctionsService,
    private store: Store,
    private puzzleFrameworkService: PuzzleFrameworkService,
    private keyboardService: PuzzleKeyboardService
  ) {
    this.keyboardService.softKeyboardOpen$.subscribe((open) => {
      this.softKeyboardOpen = open;
    });

    this.service.initialScale.subscribe(() => {
      this.initialScaleFull();
    });

    this.service.autoPan.subscribe((zoomBox: AutoZoomBox) => {
      this.panIntoView(zoomBox);
    });

    // on printZoom event, scale the puzzle to DIN A4 and zoom.
    // We could maybe catch the print events and trigger this event:
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeprint_event
    this.printZoom$ = this.service.zoomAndPrint.subscribe(async () => {
      // console.log("printZoomSubscription");

      // Set print class to scale Page to A4. Css is in print.scss
      const puzzleMain = document.getElementById('puzzle-screen-main');
      puzzleMain.classList.add('print-puzzle-overlay');
      puzzleMain.classList.add('print-puzzle-size');

      // Await the automatic zoom on size change
      await this.helperFunctions.delay(500);

      // console.log("zoomTo");
      // Zoom to full for the current container
      this.service.autoZoom.emit(null);

      // Print
      await this.helperFunctions.delay(500);
      window.print();

      // Remove print class
      puzzleMain.classList.remove('print-puzzle-size');

      // Await the automatic zoom on size change
      await this.helperFunctions.delay(200);

      // Zoom back to full
      this.service.autoZoom.emit(null);

      // Remove overlay after resizing
      await this.helperFunctions.delay(200);
      puzzleMain.classList.remove('print-puzzle-overlay');
    });

    /*
     * Send zoomBox = null to zoom to full board.
     * Send zoomBox = {left: -1, right: -1, top: -1, bottom: -1} to zoom out a bit, but not full.
     * -> This is limited or disabled by minAutoZoomOut[Mobile].
     */
    this.autoZoom$ = this.service.autoZoom.subscribe((zoomBox: AutoZoomBox | null) => {
      let zoomFull = zoomBox === null;

      let zoomOut =
        !!zoomBox && zoomBox.left === -1 && zoomBox.right === -1 && zoomBox.top === -1 && zoomBox.bottom === -1;

      // If no word position is given we zoom out to the board
      if (zoomOut || zoomFull) {
        // console.log(`zoomOut: ${zoomOut}, zoomFull: ${zoomFull}`);
        zoomBox = {
          left: this.service.showWordLengths ? -30 : 0,
          right: this.boardWidth,
          top: this.service.showWordLengths ? -30 : 0,
          bottom: this.boardHeight,
        };
      }

      let minZoom = 0;

      // If we zoom out we do it by the setup factor, unless we want full zoom
      if (zoomOut && !zoomFull) {
        // console.log("zoomOut, but not full");
        minZoom = this.helperFunctions.isMobileDevice()
          ? this.puzzleFrameworkService.puzzleFrameworkConfig.minAutoZoomOutMobile
          : this.puzzleFrameworkService.puzzleFrameworkConfig.minAutoZoomOut || 0;
      }
      // console.log(`minZoom: ${minZoom}`);

      // If minZoom is -1 we don't want to zoom out and stop here.
      if (minZoom === -1) {
        // console.log("minZoom === -1");
        // console.log("zoom is disabled");
        return;
      }
      this.zoomTo(zoomBox, minZoom, zoomOut, zoomFull);
      // };

      // /**
      //  * Special wrapper to zoom on Android devices after the keyboard has opened
      //  */
      // const zoomAndroid = () => {
      //   window.removeEventListener('resize', zoomAndroid);
      //   window.setTimeout(() => {
      //     // recalculate word position and zoom
      //     if (zoomOut) {
      //       wordPosition = {
      //         left: 0,
      //         right: this.boardWidth,
      //         top: 0,
      //         bottom: this.boardHeight,
      //       };
      //     }
      //     zoom();
      //   }, 50);
      // };
      //
      // if (this.helperFunctions.isMobileDevice() && !this.hasNativeKeyboard) {
      //   // is keyboard action is required prior to zoom
      //   if (this.service.keyboardIsOpen === zoomOut) {
      //     this.parentHeightChanged.pipe(first()).subscribe(() => {
      //       zoomAndroid();
      //     });
      //   } else {
      //     zoomAndroid();
      //   }
      // } else if (this.helperFunctions.isAndroid()) {
      //   // is keyboard action is required prior to zoom
      //   if (this.service.keyboardIsOpen === zoomOut) {
      //     window.addEventListener('resize', zoomAndroid);
      //   } else {
      //     zoomAndroid();
      //   }
      // } else {
      //   window.setTimeout(() => {
      //     zoom();
      //   }, 50);
      // }

      return;
    });

    this.hostRect = this.el.nativeElement.getBoundingClientRect();

    this.globalZoomSubscription = this.store
      .select((state) => state.puzzleZoom)
      .subscribe((puzzleZoom) => {
        if (puzzleZoom !== this.globalZoomValue) {
          this.globalZoomValue = puzzleZoom;
          this.applyGlobalZoom();
        }
      });

    this.store
      .select((state) => state.puzzleGameState.nativeKeyboard)
      .subscribe((native) => {
        this.hasNativeKeyboard = native;
      });
  }

  @HostListener('window:mousedown', ['$event'])
  public onWindowMouseDown(event: MouseEvent) {
    this.mouseDown = true;
  }

  @HostListener('window:mouseup', ['$event'])
  public onWindowMouseUp(event: MouseEvent) {
    this.mouseDown = false;
  }

  /**
   *
   */
  public ngOnDestroy() {
    this.panZoomController.dispose();
    this.globalZoomSubscription.unsubscribe();
    this.autoZoom$.unsubscribe();
    this.printZoom$.unsubscribe();
  }

  /**
   *
   */
  public initialScaleFull() {
    if (!this.service.dontScale) {
      // Set a timeout to make sure everything has loaded.
      setTimeout(() => {
        this.zoomTo(
          {
            left: this.service.showWordLengths ? -30 : 0,
            right: this.boardWidth,
            top: this.service.showWordLengths ? -30 : 0,
            bottom: this.boardHeight,
          },
          0.1,
          true,
          true
        );
      }, 100);
    }
  }

  /**
   * I think this does not mean if we zoom in or out, but if we zoom to a word or away.
   * If we are zoom to a small word and we select a large word, the value is false, but we actually physically zoom out.
   * @param zoomBox box that should be zoomed to
   * @param minZoom minimum zoom
   * @param zoomOut whether the zoom direction is in or out.
   * @param zoomFull True if we want to zoom to fully show the puzzle in the viewport.
   */
  private zoomTo(zoomBox: AutoZoomBox, minZoom = 0.1, zoomOut = true, zoomFull = false) {
    if (document.body.clientWidth === 0 || this.availableHeight === 0) {
      // since the ionic update, inactive browser tabs dont have a body width
      // as long as the tab is active, postpone the zoom operation
      if (this.scaleTimeout) {
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = undefined;
      }
      this.scaleTimeout = window.setTimeout(() => {
        this.zoomTo(zoomBox, minZoom, zoomOut, zoomFull);
      }, 100);
      return;
    }

    const marginRight = this.puzzleFrameworkService.puzzleFrameworkConfig.removeSideMargins ? 0 : 15;
    const marginLeft =
      this.puzzleFrameworkService.puzzleFrameworkConfig.removeSideMargins &&
      !document.getElementsByTagName('lib-puzzle-crossword-sidebar').length
        ? 0
        : 15;

    zoomBox.top -= 15;
    zoomBox.bottom += 15;
    zoomBox.left -= marginLeft;
    zoomBox.right += marginRight;

    const swedishBoardWidth = this.el.nativeElement.parentElement.offsetWidth;

    const swedishBoardHeight = this.availableHeight - (this.softKeyboardOpen ? 150 : 0);

    // if (!zoomOut) {
    //   // Calculates height based on iOS or iPhone Keyboard height respecting orientation.
    //   swedishBoardHeight = swedishBoardHeight - this.helperFunctions.getKeyboardHeight();
    // }

    /**
     * Moves the viewport to the selected word
     */
    const move = () => {
      // window.setTimeout(() => { alert('move'); }, 1000);
      const transform = this.panZoomController.getTransform();

      const zoomedRelativeWordPosition = {
        left: (zoomBox.left - this.boardWidth / 2) * transform.scale,
        right: (zoomBox.right - this.boardWidth / 2) * transform.scale,
        top: (zoomBox.top - this.boardHeight / 2) * transform.scale,
        bottom: (zoomBox.bottom - this.boardHeight / 2) * transform.scale,
      };

      const currentViewport = {
        left: -transform.x / transform.scale,
        top: -transform.y / transform.scale,
        right: (swedishBoardWidth - transform.x) / transform.scale,
        bottom: (swedishBoardHeight - transform.y) / transform.scale,
      };

      const boardIsVisible =
        currentViewport.left < 0 &&
        currentViewport.top < 0 &&
        currentViewport.right > this.boardWidth &&
        currentViewport.bottom > this.boardHeight;

      if (
        boardIsVisible &&
        zoomBox.top > currentViewport.top &&
        zoomBox.left > currentViewport.left &&
        zoomBox.right < currentViewport.right &&
        zoomBox.bottom < currentViewport.bottom
      ) {
        return;
      }

      const dx = 0 - transform.x + (swedishBoardWidth - this.boardWidth * transform.scale) / 2;
      const dy = 0 - transform.y + (swedishBoardHeight - this.boardHeight * transform.scale) / 2;

      this.panZoomController.moveBy(
        dx - (zoomedRelativeWordPosition.left + zoomedRelativeWordPosition.right) / 2,
        dy - (zoomedRelativeWordPosition.top + zoomedRelativeWordPosition.bottom) / 2,
        true
      );
    };

    // /**
    //  * Zooms the viewport to fit the current word to the screen
    //  */
    // const zoom = () => {
    //   const currentTransform = this.panZoomController.getTransform();
    //   const zoomBoxWidth = zoomBox.right - zoomBox.left;
    //   const zoomBoxHeight = zoomBox.bottom - zoomBox.top;
    //
    //   // Nicht größer als das skalieren
    //   const maxZoom = this.helperFunctions.isMobileDevice()
    //     ? this.puzzleFrameworkService.puzzleFrameworkConfig.maxAutoZoomInMobile
    //     : this.puzzleFrameworkService.puzzleFrameworkConfig.maxAutoZoomIn;
    //
    //   // Calculate supposed zoom
    //   const supposedZoom = Math.min(
    //     swedishBoardWidth / (zoomBoxWidth + 2),
    //     swedishBoardHeight / (zoomBoxHeight + 2),
    //     maxZoom
    //   );
    //   // Limit zoom by minZoom;
    //   const finalZoom = Math.max(minZoom, supposedZoom);
    //
    //   // Middle point of current position of zoomBox
    //   const currentPosition = {
    //     x: currentTransform.x + (zoomBoxWidth / 2 + zoomBox.left) * currentTransform.scale,
    //     y: currentTransform.y + (zoomBoxHeight / 2 + zoomBox.top) * currentTransform.scale,
    //   };
    //
    //   // Middle point of swedish board (where we want the future middle point of the zoomBox).
    //   const targetPosition = {
    //     x: swedishBoardWidth / 2,
    //     y: swedishBoardHeight / 2,
    //   };
    //
    //   // If we zoom away from a word, but we would zoom in, we don't do it.
    //   // fullZoom is also zoomOut, so on fullZoom we want to zoom, even if we would zoom in.
    //   // Also, we could ignore it on iOS, inclusive move, but it would move to center: && !this.helperFunctions.getKeyboardHeight()
    //   if (zoomOut && currentTransform.scale < finalZoom && !zoomFull) {
    //     return;
    //   }
    //   this.panZoomController.smoothZoomAbs(targetPosition.x, targetPosition.y, finalZoom);
    // };

    /**
     * Scales (pan and zoom) the viewport to fit the current word to the screen. Doesn't work properly.
     */
    const zoomAndMove = () => {
      const currentTransform = this.panZoomController.getTransform();
      const zoomBoxWidth = zoomBox.right - zoomBox.left;
      const zoomBoxHeight = zoomBox.bottom - zoomBox.top;

      // Nicht größer als das skalieren
      const maxZoom = this.helperFunctions.isMobileDevice()
        ? this.puzzleFrameworkService.puzzleFrameworkConfig.maxAutoZoomInMobile
        : this.puzzleFrameworkService.puzzleFrameworkConfig.maxAutoZoomIn;

      // Calculate supposed zoom
      const supposedZoom = Math.min(
        swedishBoardWidth / (zoomBoxWidth + 2),
        swedishBoardHeight / (zoomBoxHeight + 2),
        maxZoom
      );
      // Limit zoom by minZoom, only for zoom-out operation, not for zoom-in;
      const finalZoom = supposedZoom > currentTransform.scale ? supposedZoom : Math.max(minZoom, supposedZoom);
      // For calculating the zoomPoint
      const zoomMultiplier = supposedZoom / currentTransform.scale;

      // Middle point of current position of zoomBox
      const currentPosition = {
        x: currentTransform.x + (zoomBoxWidth / 2 + zoomBox.left) * currentTransform.scale,
        y: currentTransform.y + (zoomBoxHeight / 2 + zoomBox.top) * currentTransform.scale,
      };

      // Middle point of swedish board (where we want the future middle point of the zoomBox).
      const targetPosition = {
        x: swedishBoardWidth / 2,
        y: swedishBoardHeight / 2,
      };

      // Difference between target and current position that is used for panning.
      const panDelta = {
        x: currentPosition.x - targetPosition.x,
        y: currentPosition.y - targetPosition.y,
      };

      // Don't zoom if it would be too little difference.
      // This prevents zero division in case zoomMultiplier converges to one (no zoom).
      if (zoomMultiplier > 0.9 && zoomMultiplier < 1.2) {
        // this.panZoomController.zoomAbs(targetPosition.x, targetPosition.y, finalZoom);
        // On the initial zoom, which wants zoomFull, the move() had some issues, so if we want full zoom, we just do it
        if (!zoomFull) {
          return false;
        }
      }

      // Mouse pointer position where the "scroll action" will happen.
      const zoomPoint = {
        x: panDelta.x / (zoomMultiplier - 1) + currentPosition.x,
        y: panDelta.y / (zoomMultiplier - 1) + currentPosition.y,
      };

      this.panZoomController.smoothZoomAbs(zoomPoint.x, zoomPoint.y, finalZoom);

      return true;
    };

    // If scaling is necessary, scale, otherwise move immediately
    if (!zoomAndMove()) {
      move();
    }

    // // If we zoomOut (away from word) we don't want to move as we would move to the center of the board.
    // // But if we want to zoom to full view we want to move to the center.
    // // We could maybe move on iOS, but it would move to the center: || this.helperFunctions.getKeyboardHeight()
    // if (!zoomOut || zoomFull) {
    //   move();
    // }
    // window.setTimeout(zoom, 500);
  }

  /**
   *
   */
  public ngOnInit() {
    // const observer = new ResizeObserver((entries) => {
    //   this.parentHeight = this.el.nativeElement.parentElement.offsetHeight;
    //   if (this.parentHeightChangedTimeout) {
    //     clearTimeout(this.parentHeightChangedTimeout);
    //     this.parentHeightChangedTimeout = undefined;
    //   }
    //   // this.parentHeightChangedTimeout = window.setTimeout(() => {
    //   //   this.parentHeightChanged.emit(this.parentHeight);
    //   //   this.parentHeightChangedTimeout = undefined;
    //   // }, 100);
    // });
    // observer.observe(this.el.nativeElement.parentElement);

    const observer = new ResizeObserver((entries) => {
      this.initialScaleFull();
    });
    observer.observe(this.el.nativeElement);
    observer.observe(this.el.nativeElement.parentElement);
    this.initPanZoom();
  }

  //
  //
  // @HostListener('document:keyup', ['$event'])
  // public onKeyUp(event: KeyboardEvent) {
  //   this.zoomKeyPressed = event.metaKey || event.ctrlKey;
  // }
  //
  // @HostListener('document:keydown', ['$event'])
  // public onKeyDown(event: KeyboardEvent) {
  //   this.zoomKeyPressed = event.metaKey || event.ctrlKey;
  // }

  /**
   *
   */
  private initPanZoom() {
    const options: PanZoomOptions = {};
    options.onTouch = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        if (event.stopPropagation) {
          event.stopPropagation();
        }
        event.preventDefault();
      }
      return false;
    };
    options.zoomDoubleClickSpeed = 1;
    options.filterKey = () => true;

    options.disableKeyboardInteraction = true;

    // options.beforeWheel = (event: WheelEvent) => {
    //   return !(event.metaKey || event.ctrlKey);
    // };

    options.autocenter = false;

    options.maxZoom = this.puzzleFrameworkService.puzzleFrameworkConfig.zoomInLimit;
    options.minZoom = this.puzzleFrameworkService.puzzleFrameworkConfig.zoomOutLimit;

    options.beforeMouseDown = (e) => {
      return this.deactivate || (this.shiftPan && !e.shiftKey);
    };

    options.beforeWheel = (e) => {
      return this.deactivate || this.mouseDown;
    };

    options.bounds = true;
    options.boundsPadding = 0.1;

    // https://github.com/anvaka/panzoom#zoom-speed
    // options.zoomSpeed = 0.025;
    options.zoomSpeed = 1;
    // options.pinchSpeed = 10;

    options.smoothScroll = true;

    this.panZoomController = panzoom(this.el.nativeElement, options);

    // this.panZoomController.on('pan', ($event) => {
    // });

    this.panZoomController.on('transform', () => {
      if (this.transformTimeout) {
        clearTimeout(this.transformTimeout);
      }
      this.transformTimeout = window.setTimeout(() => {
        this.checkBounds();
      }, 100);
    });

    this.panZoomController.on('zoom', () => {
      // on zoom, compare with global zoom and update global zoom if value differs
      const transform = this.panZoomController.getTransform();
      if (this.globalZoomValue !== transform.scale) {
        this.globalZoomValue = transform.scale;
        // if(this.globalZoomValue < this.config.get('zoomOutLimit')){
        //   this.globalZoomValue = this.config.get('zoomOutLimit');
        // } else if(this.globalZoomValue > this.config.get('zoomInLimit')) {
        //   this.globalZoomValue = this.config.get('zoomInLimit');
        // }
        this.setGlobalZoom(this.globalZoomValue);
      }
    });

    this.initialized = true;

    this.applyGlobalZoom();
  }

  private checkBounds() {
    const currentTransform = this.panZoomController.getTransform();
    const parentWidth = this.el.nativeElement.parentElement.offsetWidth;
    const parentHeight = this.el.nativeElement.parentElement.offsetHeight;

    const clientWidth = this.boardWidth * currentTransform.scale;
    const clientHeight = this.boardHeight * currentTransform.scale;

    if (
      clientWidth + currentTransform.x <= 0 ||
      clientHeight + currentTransform.y <= 0 ||
      currentTransform.x >= parentWidth ||
      currentTransform.y >= parentHeight
    ) {
      this.zoomTo({
        left: 0,
        right: this.boardWidth,
        top: 0,
        bottom: this.boardHeight,
      });
    }
  }

  /**
   * @param zoomValue value to set
   */
  private setGlobalZoom(zoomValue: number) {
    if (this.setGlobalZoomTimeout) {
      clearTimeout(this.setGlobalZoomTimeout);
    }

    this.setGlobalZoomTimeout = window.setTimeout(() => {
      this.store.dispatch(
        new SetZoom({
          zoom: this.globalZoomValue,
          min: this.puzzleFrameworkService.puzzleFrameworkConfig.zoomOutLimit,
          max: this.puzzleFrameworkService.puzzleFrameworkConfig.zoomInLimit,
        })
      );
    }, 50);
  }

  /**
   *
   */
  private applyGlobalZoom() {
    if (!this.initialized) {
      return;
    }
    const transform = this.panZoomController.getTransform();
    if (this.globalZoomValue === 0) {
      return;
    }
    this.panZoomController.zoomAbs(
      this.el.nativeElement.parentElement.offsetWidth / 2,
      this.el.nativeElement.parentElement.offsetHeight / 2,
      this.globalZoomValue
    );
  }

  @HostListener('touchmove', ['$event'])
  preventIOSViewportZoom(event: KeyboardEvent) {
    event.preventDefault();
  }

  private panIntoView(zoomBox: AutoZoomBox) {
    const currentTransform = this.panZoomController.getTransform();

    const scaledZoomBox: AutoZoomBox = {
      bottom: zoomBox.bottom * currentTransform.scale,
      left: zoomBox.left * currentTransform.scale,
      right: zoomBox.right * currentTransform.scale,
      top: zoomBox.top * currentTransform.scale,
    };

    const boardHeight = this.el.nativeElement.parentElement.offsetHeight;
    const boardWidth = this.el.nativeElement.parentElement.offsetWidth;

    const moveCoordinates = {
      x: currentTransform.x,
      y: currentTransform.y,
    };

    switch (true) {
      case scaledZoomBox.top + currentTransform.y < 0:
        moveCoordinates.y -= scaledZoomBox.top + currentTransform.y;
        break;
      case boardHeight - scaledZoomBox.bottom - currentTransform.y < 0:
        moveCoordinates.y += boardHeight - scaledZoomBox.bottom - currentTransform.y;
        break;
    }

    switch (true) {
      case scaledZoomBox.left + currentTransform.x < 0:
        moveCoordinates.x -= scaledZoomBox.left + currentTransform.x;
        break;
      case boardWidth - scaledZoomBox.right - currentTransform.x < 0:
        moveCoordinates.x += boardWidth - scaledZoomBox.right - currentTransform.x;
        break;
    }

    this.panZoomController.smoothMoveTo(moveCoordinates.x, moveCoordinates.y);
  }
}
